if (!customElements.get('intersection-loader')) {
  const LOADER_DEBUG = /(?:^|[?&])plp-debug(?:=|&|$)/.test(window.location.search);
  const log = LOADER_DEBUG ? console.log.bind(console) : () => {};

  let pageHasScrolled = false;
  const pendingLoaders = new Set();
  const activatePendingLoaders = () => {
    if (pageHasScrolled) return;
    pageHasScrolled = true;
    pendingLoaders.forEach((loader) => loader._activateObserver());
    pendingLoaders.clear();
  };
  window.addEventListener('scroll', activatePendingLoaders, { passive: true, once: true });
  if (window.requestIdleCallback) {
    requestIdleCallback(activatePendingLoaders, { timeout: 10000 });
  } else {
    setTimeout(activatePendingLoaders, 8000);
  }

  const OVERLAY_EAGER_CLASS =
    'sp-absolute sp-top-[-15%] sp-left-[-15%] sp-w-[130%] sp-max-w-none sp-h-[130%] sp-object-cover sp-pointer-events-none';

  class IntersectionLoader extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.classList.add('intersection-loader--skeleton');

      if (this.dataset.priority === 'true' || this.dataset.eager === 'true') {
        this._load();
        return;
      }

      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this._load();
        });
      }, {
        rootMargin: this.dataset.rootMargin || '0px',
      });

      if (pageHasScrolled) this._observer.observe(this);
      else pendingLoaders.add(this);
    }

    _activateObserver() {
      if (this._observer) this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      pendingLoaders.delete(this);
    }

    async _load() {
      if (this._loading || this._loaded) return;
      this._loading = true;
      if (this._observer) this._observer.disconnect();

      const url = this.dataset.url;
      const sectionId = this.dataset.sectionId;
      if (!url || !sectionId) {
        console.error('[intersection-loader] missing data-url or data-section-id, removing element', { url, sectionId, el: this });
        this.remove();
        return;
      }

      const fetchUrl = `${url}${url.indexOf('?') === -1 ? '?' : '&'}section_id=${sectionId}`;
      log('[intersection-loader] fetching', fetchUrl);

      try {
        const fetchOptions = this.dataset.priority === 'true' ? { priority: 'high' } : undefined;
        const response = await fetch(fetchUrl, fetchOptions);
        if (!response.ok) throw new Error(`intersection-loader: fetch failed with status ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector(`#shopify-section-${sectionId}`);

        if (!content || !content.children.length) {
          console.error(`[intersection-loader] no #shopify-section-${sectionId} wrapper in the response for`, fetchUrl);
          this.remove();
          return;
        }

        const extra = this._parseExtra();
        const fragment = document.createDocumentFragment();
        Array.from(content.children).forEach((child) => {
          Object.keys(extra).forEach((key) => child.setAttribute(`data-${key}`, extra[key]));
          fragment.appendChild(child);
        });

        this._applyPriorityTreatment(fragment);

        const insertedNodes = Array.from(fragment.children);
        const target = this.parentNode;
        this.replaceWith(fragment);
        if (target) {
          target.dispatchEvent(
            new CustomEvent('intersection:loaded', { bubbles: true, detail: { loader: this, nodes: insertedNodes } })
          );
        }
      } catch (err) {
        console.error('[intersection-loader] load failed for', fetchUrl, err);
        this.remove();
      } finally {
        this._loaded = true;
        this._loading = false;
      }
    }

    _applyPriorityTreatment(fragment) {
      if (this.dataset.priority !== 'true') {
        fragment.querySelectorAll('img[aria-hidden]').forEach((img) => img.remove());
        return;
      }

      const slides = Array.from(fragment.querySelectorAll('[data-product-card-slide]'));
      const activeSlide = slides.find((slide) => slide.querySelector('img.sp-opacity-100'));
      if (!activeSlide) {
        fragment.querySelectorAll('img[aria-hidden]').forEach((img) => img.remove());
        return;
      }

      slides.forEach((slide) => {
        if (slide !== activeSlide) {
          slide.querySelectorAll('img[aria-hidden]').forEach((img) => img.remove());
          return;
        }
        slide.querySelectorAll('img').forEach((img) => {
          img.loading = 'eager';
          img.fetchPriority = 'high';
          if (img.hasAttribute('aria-hidden')) img.className = OVERLAY_EAGER_CLASS;
        });
      });
    }

    _parseExtra() {
      const raw = this.dataset.extra;
      if (!raw) return {};
      const result = {};
      raw.split(',').forEach((pair) => {
        const idx = pair.indexOf(':');
        if (idx === -1) return;
        result[pair.slice(0, idx).trim()] = pair.slice(idx + 1).trim();
      });
      return result;
    }
  }

  customElements.define('intersection-loader', IntersectionLoader);
}
